// @flow

import React, { useState } from 'react';
import styled from 'styled-components';
import { withLocalePrefix } from 'shared/utils/translation';
import { Collapse } from '@grnhse/seedling/lib/azalea/components/collapse';
import Checkbox from '@grnhse/seedling/lib/azalea/components/checkbox_with_label';
import Input from '@grnhse/seedling/lib/azalea/components/forms/input';
import { colors } from '@grnhse/seedling/lib/azalea/constants';
import {
  BodyText,
  SectionHeader,
  SubSectionHeader,
} from '@grnhse/seedling/lib/azalea/typography';
import { Link } from '@grnhse/seedling/lib/birch/typography';

const t = withLocalePrefix('availability.candidate_pronouns');

type PronounOption = {
  value: string,
  isSelfDescribe: boolean,
};

type Props = {
  defaultPronounsOptions: Array<string>,
  pronouns: Array<PronounOption>,
  isMobile: boolean,
};

const CandidatePronounsField = (props: Props) => {
  const [showTips, setShowTips] = useState(false);
  const [pronouns, setPronouns] = useState(props.pronouns);

  const initialSelfDescribeValue = () => {
    const selfDescribePronoun = pronouns.find((p) => p.isSelfDescribe);
    if (selfDescribePronoun) {
      return selfDescribePronoun.value;
    }

    return '';
  };

  const [selfDescribeValue, setSelfDescribeValue] = useState(
    initialSelfDescribeValue()
  );

  const toggleTips = (event) => {
    event.preventDefault();
    setShowTips(!showTips);
  };

  const examples = () => t('tips.examples').replace(/\n/g, '<br>');

  const isChecked = (option) => {
    if (option === 'self_describe') {
      return pronouns.some((p) => p.isSelfDescribe);
    } else {
      return pronouns.some((p) => p.value === option);
    }
  };

  const updateCheckbox = (option, isSelfDescribe) => {
    if (isChecked(option)) {
      let remainingPronouns;
      if (isSelfDescribe) {
        remainingPronouns = pronouns.filter((p) => !p.isSelfDescribe);
      } else {
        remainingPronouns = pronouns.filter((p) => p.value !== option);
      }

      setPronouns(remainingPronouns);
    } else {
      const newPronoun = { value: option, isSelfDescribe };

      setPronouns([...pronouns, newPronoun]);
    }
  };

  const onCheckboxChange = (option) =>
    updateCheckbox(option, option === 'self_describe');

  return (
    <section>
      <SectionHeader>
        {t('header')} <Optional>{t('optional')}</Optional>
      </SectionHeader>
      <BodyText>
        {t('subheader')}{' '}
        {!showTips && (
          <Link className="show-pronoun-tips" href="#" onClick={toggleTips}>
            {t('tips_trigger')}
          </Link>
        )}
      </BodyText>
      <Collapse
        id="pronoun_tips"
        isOpened={showTips}
        style={{ marginTop: '10px' }}
      >
        <SubSectionHeader>{t('tips.header')}</SubSectionHeader>
        <BodyText>{t('tips.body')}</BodyText>
        <SubSectionHeader>{t('tips.example_header')}</SubSectionHeader>
        <BodyText dangerouslySetInnerHTML={{ __html: examples() }} />
        <Link className="show-pronoun-tips" href="#" onClick={toggleTips}>
          {t('tips.hide')}
        </Link>
      </Collapse>

      <CheckboxWrapper isMobile={props.isMobile}>
        {[...props.defaultPronounsOptions, 'self_describe'].map((option) => (
          <Checkbox
            label={t(`options.${option}`)}
            checked={isChecked(option)}
            name={`pronouns_attributes[${option}]`}
            value={option}
            onChange={() => onCheckboxChange(option)}
            key={option}
          />
        ))}

        {isChecked('self_describe') && (
          <Input
            value={selfDescribeValue}
            placeholder={t('options.placeholder')}
            name={'pronouns_attributes[self_describe_input]'}
            onChange={(e) => setSelfDescribeValue(e.currentTarget.value)}
          />
        )}
      </CheckboxWrapper>
    </section>
  );
};

const CheckboxWrapper = styled.div`
  width: 400px;
  max-width: 100%;
  margin-top: 10px;

  input[type='checkbox'] {
    margin-bottom: 14px;
  }

  input[type='text'] {
    margin-left: 18px;

    ${(props) =>
      props.isMobile &&
      `
      max-width: 100%;
      margin-left: 0;
    `}
  }
`;

const Optional = styled.span`
  color: ${colors.mediumGrey};
`;

export default CandidatePronounsField;
